import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, CreditCard, Loader2, ShieldCheck, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSitePath } from "../context/SiteContext";
import { toast } from "sonner";
import { api, apiError, money } from "../lib/api";
import { useBooking } from "../context/BookingContext";
import { CardBrands, MethodLogo, methodTitle } from "../components/PayLogos";

const Terms = ({ ch }) => {
  const r = ch.quote.rate;
  const lines = [...(r.payment_terms || []), ...(ch.cancellation || [])];
  if (!lines.length) return null;
  return (
    <ul className="mt-4 space-y-1.5 text-sm text-ink/70" data-testid="payment-terms">
      {lines.map((l) => <li key={l} className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{l}</li>)}
    </ul>
  );
};

const MethodChoice = ({ m, bank, current, onPick }) => (
  <button type="button" onClick={() => onPick(m)} data-testid={`pay-method-${m}`}
    className={`flex min-h-[112px] flex-col justify-between gap-3 rounded-2xl border p-4 text-left transition-colors ${current === m ? "border-brand bg-brand-light/40 ring-1 ring-brand" : "border-ink/10 hover:border-brand/40"}`}>
    <span className="flex items-center justify-between gap-2"><MethodLogo m={m} bank={bank} />{m !== "mpesa" && <CardBrands />}</span>
    <span><span className="block font-medium text-ink">{methodTitle(m, bank)}</span>
      <span className="text-sm text-ink/60">{m === "mpesa" ? "Prompt on your phone" : "Visa, Mastercard and more"}</span></span>
  </button>
);

const Choice = ({ value, current, onPick, title, sub, testid }) => (
  <button type="button" onClick={() => onPick(value)} data-testid={testid}
    className={`flex-1 rounded-2xl border p-4 text-left transition-colors ${current === value ? "border-brand bg-brand-light/40" : "border-ink/10 hover:border-brand/40"}`}>
    <span className="block font-medium text-ink">{title}</span><span className="text-sm text-ink/60">{sub}</span>
  </button>
);

const PayForm = ({ ch, onStart, onCard, busy, option }) => {
  const [phone, setPhone] = useState(ch.phone || "");
  const [method, setMethod] = useState(ch.methods[0] || "");
  const banks = ch.method_banks || {};
  useEffect(() => {
    if (ch.status === "failed" && ch.method === "mpesa" && ch.methods.includes("mpesa")) {
      setMethod("mpesa");
    }
  }, [ch.status, ch.method, ch.error, ch.methods]);
  const due = option === "full" && ch.total ? ch.total : ch.kind === "balance" || !ch.total ? ch.amount : ch.deposit;
  if (!ch.methods.length) {
    return <p className="mt-6 rounded-2xl bg-gold/15 p-4 text-sm text-ink" data-testid="no-payment-methods">Online payment in {ch.currency} is not set up for this hotel yet. Please contact the hotel to complete your booking.</p>;
  }
  return (
    <div className="mt-6 space-y-4">
      {ch.methods.length > 1 && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="pay-method-choice">
          {ch.methods.map((m) => <MethodChoice key={m} m={m} bank={banks[m]} current={method} onPick={setMethod} />)}
        </div>
      )}
      {method === "mpesa" ? (
        <form onSubmit={(e) => { e.preventDefault(); onStart(phone); }} className="space-y-3" data-testid="mpesa-form">
          <label className="field">
            <span className="field-label">M-Pesa phone number</span>
            <input className="field-input" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0712 345 678" data-testid="mpesa-phone-input" />
          </label>
          {ch.error && <p className="text-sm text-red-600" data-testid="payment-error">{ch.error}</p>}
          <button className="btn-gold w-full" disabled={busy} data-testid="mpesa-pay-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}Pay {money(due, ch.currency)} with M-Pesa
          </button>
        </form>
      ) : (
        <div className="space-y-3">
          {ch.error && <p className="text-sm text-red-600" data-testid="payment-error">{ch.error}</p>}
          <button className="btn-gold w-full" disabled={busy} onClick={() => onCard(method)} data-testid="card-pay-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}Pay {money(due, ch.currency)} by card
          </button>
          <p className="text-xs text-ink/50">You'll go to the secure {banks[method] || methodTitle(method).replace("Card via ", "")} payment page and come back here when done.</p>
        </div>
      )}
    </div>
  );
};

const Waiting = ({ ch, onSimulate }) => (
  <div className="mt-6 rounded-2xl bg-linen p-5 text-sm" data-testid="payment-waiting">
    <p className="flex items-center gap-2 font-medium text-ink"><Loader2 className="h-4 w-4 animate-spin text-brand" />
      {ch.status === "pending" ? (ch.method === "mpesa" ? `Check your phone (${ch.phone}) and enter your M-Pesa PIN` : "Waiting for the card payment to be confirmed…") : "Confirming your reservation with the hotel…"}
    </p>
    {ch.mode === "simulated" && ch.status === "pending" && (
      <button className="btn-outline mt-4" onClick={onSimulate} data-testid="simulate-payment-button">Simulate successful payment (demo)</button>
    )}
  </div>
);

export default function Payment() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const sp = useSitePath();
  const qc = useQueryClient();
  const { clearItinerary } = useBooking();
  const [busy, setBusy] = useState(false);
  const [option, setOption] = useState("deposit");
  const { data, error } = useQuery({
    queryKey: ["checkout", cid],
    queryFn: () => api.get(`/checkout/${cid}`).then((r) => r.data),
    retry: (n, e) => !(e?.response?.status >= 400 && e?.response?.status < 500) && n < 3,
    refetchInterval: (q) => (["pending", "paid", "committing"].includes(q.state.data?.checkout.status) ? 4000 : false),
  });
  useEffect(() => {
    if (data?.reservation) {
      clearItinerary();
      navigate(sp(`/confirmation/${data.reservation.ref}`), { state: { reservation: data.reservation }, replace: true });
    }
  }, [data, clearItinerary, navigate]);

  const act = async (path, body) => {
    setBusy(true);
    try {
      const res = (await api.post(`/checkout/${cid}/${path}`, body)).data;
      if (res.redirect_url) {
        window.location.assign(res.redirect_url);
        return;
      }
      qc.setQueryData(["checkout", cid], res);
    } catch (e) {
      toast.error(apiError(e));
      qc.invalidateQueries({ queryKey: ["checkout", cid] });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="container-x pt-16 text-red-600" data-testid="payment-load-error">{apiError(error)}</div>;
  if (!data) return <div className="grid min-h-[50vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  const ch = data.checkout;
  const q = ch.quote;
  const isBalance = ch.kind === "balance";
  const canChoose = !isBalance && ch.total > ch.deposit && ["awaiting_payment", "failed"].includes(ch.status);
  const due = canChoose ? (option === "full" ? ch.total : ch.deposit) : ch.amount;
  return (
    <div className="container-x max-w-3xl pt-12" data-testid="payment-page">
      <p className="eyebrow">Secure your booking</p>
      <h1 className="mt-2 font-serif text-4xl text-ink sm:text-5xl">{isBalance ? "Pay your balance" : "Secure your stay"}</h1>
      {ch.mode === "simulated" && ch.method === "mpesa" && ch.methods.includes("mpesa") && (
        <p className="mt-4 flex gap-2 rounded-xl bg-gold/15 px-4 py-3 text-sm text-ink" data-testid="payment-demo-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          Demo mode: M-Pesa is not connected yet, so no money moves.{isBalance ? " The balance will be marked as paid on this site only (nothing is posted to eRes)." : " Your room is held in eRes and the booking will be confirmed with a \"payment simulated\" note."}
        </p>
      )}
      <div className="mt-8 rounded-3xl border border-brand/10 bg-white p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-serif text-2xl text-ink">{q.hotel.name}</p>
            <p className="text-sm text-ink/60">{q.room.name} · {q.rate.name} · {q.nights} night(s)</p>
          </div>
          <div className="sm:text-right">
            <p className="text-xs text-ink/50">Total stay {money(q.total, q.currency)}</p>
            <p className="font-mono text-2xl text-ink" data-testid="deposit-amount">{money(due, ch.currency)}</p>
            <p className="text-xs text-ink/50">due now</p>
          </div>
        </div>
        <Terms ch={ch} />
        {canChoose && (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-testid="pay-option-choice">
            <Choice value="deposit" current={option} onPick={setOption} testid="pay-option-deposit" title={`Deposit ${money(ch.deposit, ch.currency)}`} sub="As per the hotel's policy; pay the rest later" />
            <Choice value="full" current={option} onPick={setOption} testid="pay-option-full" title={`Full amount ${money(ch.total, ch.currency)}`} sub="Nothing left to pay" />
          </div>
        )}
        {!isBalance && ["awaiting_payment", "failed", "pending"].includes(ch.status) && (
          <p className="mt-4 rounded-xl bg-linen px-4 py-2.5 text-sm text-ink/75" data-testid="hold-until">Your room is held until {new Date(ch.held_until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}. Unpaid holds are released automatically.</p>
        )}
        {ch.status === "expired" && (
          <div className="mt-6 flex flex-col items-start gap-4 rounded-2xl bg-linen p-5 text-sm text-ink" data-testid="hold-expired">
            <p>This room hold has expired because the deposit was not paid in time, and the room has been released.</p>
            <Link to={sp("/hotels")} className="btn-primary" data-testid="hold-expired-search">Search again</Link>
          </div>
        )}
        {["awaiting_payment", "failed"].includes(ch.status) && <PayForm ch={ch} busy={busy} option={option} onStart={(phone) => act("mpesa", { phone, option })} onCard={(gateway) => act("card", { gateway, option })} />}
        {["pending", "paid", "committing"].includes(ch.status) && <Waiting ch={ch} onSimulate={() => act("simulate")} />}
        {ch.status === "commit_failed" && (
          <div className="mt-6 rounded-2xl bg-red-50 p-5 text-sm text-red-800" data-testid="commit-failed">
            Your payment{ch.receipt && ch.receipt !== "SIMULATED" ? ` (receipt ${ch.receipt})` : ""} was received, but the hotel system could not confirm the booking: {ch.error}. Please contact the hotel and quote this receipt.
          </div>
        )}
        {ch.status === "committed" && <p className="mt-6 flex items-center gap-2 text-brand"><CheckCircle2 className="h-5 w-5" />Confirmed</p>}
      </div>
      <Link to={isBalance ? "/find-reservation" : "/details"} className="btn-ghost mt-6" data-testid="payment-back-link">Back</Link>
    </div>
  );
}
